import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { LuGauge, LuPencil, LuTrash2 } from "react-icons/lu";
import { FlowTestEntryModal } from "../fieldtech/FlowTestEntryModal";

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const statusBadge = (status) => {
  if (status === "passed") {
    return <span style={{ color: colors.green, fontWeight: "600" }}>Passed</span>;
  }
  if (status === "failed") {
    return <span style={{ color: colors.red, fontWeight: "600" }}>Failed</span>;
  }
  return <span style={{ color: colors.slate }}>In Progress</span>;
};

export function FlowTestsPage() {
  const [flowTests, setFlowTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enteringFlowTest, setEnteringFlowTest] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/flow-tests/`)
      .then((res) => setFlowTests(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (ft) => {
    if (!window.confirm(`Delete the flow test for ${ft.site_name}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/flow-tests/${ft.id}/`);
      load();
    } catch {
      window.alert("Something went wrong deleting this flow test. Please try again.");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Fire Hose Reel Flow Tests</div>
      </div>

      <div style={styles.content}>
        <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "16px" }}>
          Flow/pressure tests at the Most Disadvantaged Fire Hose Reel (MDFHR),
          automatically created for annual inspections covering a Fire Hose
          Reels EFSM. AS 1851 requires a minimum flow of 0.33 L/s; AS 2441
          requires that flow at 220 kPa ± 10% (198–242 kPa).
        </div>

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : (
          <div style={styles.card}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: "2fr 1fr 1fr 1.4fr 1.4fr 190px" }}>
              <div>Site</div>
              <div>Status</div>
              <div>Assigned To</div>
              <div>Flow Rate</div>
              <div>Pressure</div>
              <div></div>
            </div>
            {flowTests.length === 0 ? (
              <div style={{ ...styles.loading, display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "40px" }}>
                <LuGauge size={28} style={{ color: "#cbd5e1" }} />
                No flow tests yet.
              </div>
            ) : (
              flowTests.map((ft) => (
                <div key={ft.id} style={{ ...styles.tableRow(false), gridTemplateColumns: "2fr 1fr 1fr 1.4fr 1.4fr 130px" }}>
                  <div>
                    <div style={{ fontWeight: "500" }}>{ft.site_name}</div>
                    <div style={{ fontSize: "12px", color: "#999" }}>
                      {ft.site_address} — {formatDate(ft.inspection_date)}
                    </div>
                  </div>
                  <div style={{ fontSize: "13px" }}>{statusBadge(ft.status)}</div>
                  <div style={{ fontSize: "13px" }}>
                    {ft.assigned_to_name || <span style={{ color: colors.red, fontWeight: "600" }}>Unassigned</span>}
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {ft.flow_rate_ls != null ? (
                      <span style={{ color: ft.flow_passes ? colors.green : colors.red, fontWeight: "600" }}>
                        {ft.flow_rate_ls} L/s
                      </span>
                    ) : "—"}
                    <div style={{ fontSize: "11px", color: "#999" }}>min {ft.min_flow_ls} L/s</div>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    {ft.pressure_kpa != null ? (
                      <span style={{ color: ft.pressure_passes ? colors.green : colors.red, fontWeight: "600" }}>
                        {ft.pressure_kpa} kPa
                      </span>
                    ) : "—"}
                    <div style={{ fontSize: "11px", color: "#999" }}>{ft.min_pressure_kpa}–{ft.max_pressure_kpa} kPa</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {ft.status === "in_progress" ? (
                      <button
                        onClick={() => setEnteringFlowTest(ft)}
                        style={{
                          fontSize: "12px", fontWeight: "600", padding: "5px 12px", borderRadius: "6px",
                          border: "none", cursor: "pointer",
                          backgroundColor: colors.blue, color: "white",
                        }}
                      >
                        Enter Readings
                      </button>
                    ) : (
                      <button
                        onClick={() => setEnteringFlowTest(ft)}
                        title="Edit readings"
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "28px", height: "28px", borderRadius: "6px",
                          border: `1px solid ${colors.border}`, backgroundColor: "white",
                          color: colors.blue, cursor: "pointer",
                        }}
                      >
                        <LuPencil size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(ft)}
                      title="Delete flow test"
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: "28px", height: "28px", borderRadius: "6px",
                        border: `1px solid ${colors.border}`, backgroundColor: "white",
                        color: colors.slate, cursor: "pointer",
                      }}
                    >
                      <LuTrash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {enteringFlowTest && (
        <FlowTestEntryModal
          flowTest={enteringFlowTest}
          onClose={() => { setEnteringFlowTest(null); load(); }}
          onSaved={load}
        />
      )}
    </div>
  );
}
